// src/context/WebAuthContext.js
import React, { useState, useEffect, useContext, createContext } from "react";
import { 
  onAuthStateChanged, 
  getAuth, 
  fetchSignInMethodsForEmail,
  sendEmailVerification
} from "firebase/auth";
import { getFirestore, doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import firebase_app from "../firebase/config";
import signIn from "../firebase/auth/signin";
import signUp from "../firebase/auth/signup";
import logout from "../firebase/auth/logout";

const auth = getAuth(firebase_app);
const db = getFirestore(firebase_app);

const WebAuthContext = createContext();

export const useWebAuth = () => useContext(WebAuthContext);

export const WebAuthProvider = ({ children }) => {
  const [webUser, setWebUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(null); // { email, userId }

  const isWebUser = () => {
    return !window.Telegram?.WebApp?.initDataUnsafe?.user && webUser !== null;
  };

  const checkEmailExists = async (email) => {
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      return methods.length > 0;
    } catch (error) {
      console.error("Error checking email:", error);
      return false;
    }
  };

  const resendVerificationEmail = async () => {
    if (!auth.currentUser) {
      return { success: false, error: "Пользователь не найден" };
    }
    
    try {
      await sendEmailVerification(auth.currentUser);
      return { success: true };
    } catch (error) {
      console.error("Error resending verification:", error);
      return { success: false, error: error.message };
    }
  };

  const loginWithEmail = async (email, password) => {
    console.log("🔐 WebAuthContext: Starting login for:", email);
    setLoading(true);
    setError(null);
    try {
      const { result, error } = await signIn(email, password);
      if (error) throw error;
      
      await result.user.reload();
      const isVerified = result.user.emailVerified;
      
      if (!isVerified) {
        setPendingVerification({ email: email, userId: result.user.uid });
        return { success: false, error: "email_not_verified", userId: result.user.uid };
      }
      
      const userDoc = await getDoc(doc(db, "webUsers", result.user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setWebUser({ ...result.user, ...userData, isWebUser: true });
      } else {
        setWebUser({ ...result.user, isWebUser: true });
      }
      setEmailVerified(true);
      setPendingVerification(null);
      
      return { success: true };
    } catch (err) {
      let errorMessage = err.message;
      if (err.code === 'auth/invalid-credential') {
        errorMessage = 'Неверный email или пароль';
      }
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Регистрация - НЕ разлогиниваемся
  const registerWithEmail = async (email, password, fullName, phoneNumber) => {
    console.log("🔐 WebAuthContext: Starting registration for:", email);
    setLoading(true);
    setError(null);
    try {
      const emailExists = await checkEmailExists(email);
      if (emailExists) {
        throw new Error("Пользователь с таким email уже существует");
      }
      
      const { result, error } = await signUp(email, password, fullName, phoneNumber);
      
      if (error) throw error;
      if (!result || !result.user) throw new Error("Registration failed");
      
      console.log("✅ User registered:", result.user.uid);
      
      // Сохраняем информацию о pending verification НЕ разлогиниваясь
      setPendingVerification({ email: email, userId: result.user.uid });
      
      return { 
        success: true, 
        message: "Письмо с подтверждением отправлено на ваш email",
        userId: result.user.uid,
        email: email
      };
    } catch (err) {
      console.error("Registration error:", err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const logoutUser = async () => {
    await logout();
    setWebUser(null);
    setEmailVerified(false);
    setPendingVerification(null);
  };

  const clearPendingVerification = () => {
    setPendingVerification(null);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("🔐 WebAuthContext: Auth state changed", user?.uid || "No user");
      
      if (user && !window.Telegram?.WebApp?.initDataUnsafe?.user) {
        await user.reload();
        const isVerified = user.emailVerified;
        
        if (isVerified) {
          const userDoc = await getDoc(doc(db, "webUsers", user.uid));
          if (userDoc.exists()) {
            setWebUser({ ...user, ...userDoc.data(), isWebUser: true });
          } else {
            setWebUser({ ...user, isWebUser: true });
          }
          setEmailVerified(true);
          setPendingVerification(null);
        } else {
          // Не очищаем webUser полностью, если есть pendingVerification
          if (!pendingVerification) {
            setWebUser(null);
          }
          setEmailVerified(false);
        }
      } else if (!window.Telegram?.WebApp?.initDataUnsafe?.user) {
        if (!pendingVerification) {
          setWebUser(null);
        }
        setEmailVerified(false);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [pendingVerification]);

  return (
    <WebAuthContext.Provider value={{
      webUser,
      loading,
      error,
      emailVerified,
      pendingVerification,
      loginWithEmail,
      registerWithEmail,
      logoutUser,
      isWebUser,
      checkEmailExists,
      resendVerificationEmail,
      clearPendingVerification
    }}>
      {children}
    </WebAuthContext.Provider>
  );
};